/**
 * ConfigContext.tsx
 * Provides dynamic ward and lab-type configuration fetched from the database.
 *
 * Cache strategy: localStorage cache is served immediately on first render,
 * then refreshed from Supabase in the background (same pattern as PatientContext).
 *
 * Admin mutations (add/update/delete) are only available to users with the
 * 'admin' role — enforced at the component layer via `can(user, 'team:manage')`.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { WardConfig, LabTypeConfig, HospitalConfig, MedicationConfig, DepartmentTemplateOverride, SpecialtyFieldGroup, VitalThresholds, ComorbidityEntry } from '../types';
import {
  fetchWards, createWard, updateWard, deleteWard,
  fetchLabTypes, createLabType, updateLabType, deleteLabType,
  fetchUnitChiefs, upsertUnitChief,
  fetchHospitalConfig, upsertHospitalConfig,
  fetchMedications, createMedication, updateMedication, deleteMedication,
  seedDefaultMedications,
  fetchDepartmentTemplate, saveDepartmentTemplate, deleteDepartmentTemplate,
} from '../services/configService';
import {
  SPECIALTY_TEMPLATES,
  detectSpecialtyFromDepartment,
  type SpecialtyKey,
} from '../services/specialtyTemplates';
import { saveToStorage, loadFromStorage } from '../services/persistence';
import { supabase } from '../lib/supabase';
import { toast } from '../utils/toast';
import { useAuth } from './AuthContext';

// ─── localStorage cache keys ───
const WARD_CACHE_KEY             = 'config_wards';
const LAB_CACHE_KEY              = 'config_lab_types';
const MED_CACHE_KEY              = 'config_medications';
const UNIT_CHIEFS_CACHE_KEY      = 'config_unit_chiefs';
const HOSPITAL_CONFIG_CACHE_KEY  = 'config_hospital';

const DEFAULT_HOSPITAL_CONFIG: HospitalConfig = {
  hospitalName: 'MY HOSPITAL',
  department: 'DEPARTMENT OF ORTHOPAEDICS',
  units: ['Unit 1', 'Unit 2', 'Unit 3'],
  preOpModuleName: 'Pre-op Clearance',
  procedureListName: 'Procedure List',
  preOpChecklistTemplate: [
    'Consent', 'Pre-OP Order', 'Inj. Cefuroxime', 'Part Preparation (Shave)',
    'Pre-OP X-Ray', 'C-Sample (Cross Match)', 'CBD (Catheter)', 'Implant Order', 'Things / Materials',
  ],
  showNursingNotes: false,
  showMedicationChart: false,
  showIntakeOutput: false,
  showBloodTransfusion: false,
  showWoundCare: false,
  showWoundAssessment: false,
  showRehabilitation: false,
  showNews2: true,
  customTodoShortcuts: [],
  vitalThresholds: {
    spO2Min: 90,
    hrMin: 50,
    hrMax: 120,
    sbpMin: 90,
    rrMin: 10,
    rrMax: 30,
  },
};

// ─── Fallback defaults (used when DB is unreachable and no cache exists) ───
const DEFAULT_WARDS: WardConfig[] = [
  { id: 'default-icu', name: 'ICU',    sortOrder: 0, isIcu: true,  active: true },
  { id: 'default-w1',  name: 'Ward 1', sortOrder: 1, isIcu: false, active: true },
  { id: 'default-w2',  name: 'Ward 2', sortOrder: 2, isIcu: false, active: true },
];
const DEFAULT_LAB_TYPES: LabTypeConfig[] = [
  { id: 'default-fbs',  name: 'FBS',  unit: 'mg/dL', alertHigh: 126, category: 'Diabetes',  sortOrder: 0, active: true },
  { id: 'default-ppbs', name: 'PPBS', unit: 'mg/dL', alertHigh: 200, category: 'Diabetes',  sortOrder: 1, active: true },
  { id: 'default-esr',  name: 'ESR',  unit: 'mm/hr', alertHigh: 20,  category: 'Infection', sortOrder: 0, active: true },
  { id: 'default-crp',  name: 'CRP',  unit: 'mg/L',  alertHigh: 10,  category: 'Infection', sortOrder: 1, active: true },
];

// ─── Context Shape ───
interface ConfigContextType {
  wards: WardConfig[];
  labTypes: LabTypeConfig[];
  isLoadingConfig: boolean;

  /** Detected specialty key for this department (e.g. 'orthopaedics'). */
  activeSpecialty: SpecialtyKey;
  /** Merged field groups: hospital override if present, else default template. */
  activeFieldGroups: SpecialtyFieldGroup[];
  /** The hospital-specific template override, or null if using default. */
  templateOverride: DepartmentTemplateOverride | null;
  /** Save a custom field-group layout for this hospital's department. */
  saveTemplateOverride: (groups: SpecialtyFieldGroup[]) => Promise<void>;
  /** Delete custom override, reverting to the system default. */
  resetTemplateOverride: () => Promise<void>;
  /** Names of all ICU wards (for ICU-specific styling). */
  icuWardNames: Set<string>;
  /** Lab types grouped by category, preserving sort order. */
  labTypesByCategory: Map<string, LabTypeConfig[]>;

  /** Unit → surgeon name mapping for OT list auto-fill. */
  unitChiefs: Record<string, string>;
  setUnitChief: (unit: string, name: string) => Promise<void>;

  /** Hospital name (used in PDF/Excel exports). */
  hospitalName: string;
  /** Department name (used in PDF/Excel exports). */
  department: string;
  /** Available clinical units. */
  unitOptions: string[];
  /** Label for the pre-op clearance module (e.g. "PAC Status"). */
  preOpModuleName: string;
  /** Label for the procedure/surgery list module (e.g. "OT List"). */
  procedureListName: string;
  /** Configurable pre-op checklist item labels for the Pre-Op Prep screen. */
  preOpChecklistTemplate: string[];
  /** Whether the Nursing Notes tab is enabled (admin-configurable). */
  showNursingNotes: boolean;
  /** Whether the Medication Chart (MAR) tab is enabled (admin-configurable). */
  showMedicationChart: boolean;
  /** Whether the Intake/Output documentation tab is enabled. */
  showIntakeOutput: boolean;
  /** Whether the Blood Transfusion documentation tab is enabled. */
  showBloodTransfusion: boolean;
  /** Whether the Wound Care documentation tab is enabled. */
  showWoundCare: boolean;
  /** Whether the Wound Assessment field group is shown in Patient Detail. */
  showWoundAssessment: boolean;
  /** Whether the Rehabilitation field group is shown in Patient Detail. */
  showRehabilitation: boolean;
  /** Whether the NEWS2 Early Warning Score is shown on the dashboard and vitals. */
  showNews2: boolean;
  /** Admin-defined quick-add shortcuts shown in Ward Rounds to-do section. */
  customTodoShortcuts: string[];
  /** ICU vital sign alert thresholds (SpO2, HR, BP, RR). */
  vitalThresholds: VitalThresholds;
  /** Persist updated hospital config to DB + cache. */
  saveHospitalConfig: (config: HospitalConfig) => Promise<void>;
  /** Comorbidity short→full mapping used by OCR and the comorbidity picker. */
  comorbidityMap: ComorbidityEntry[];
  /** Persist an updated comorbidity map (wraps saveHospitalConfig). */
  saveComorbidityMap: (map: ComorbidityEntry[]) => Promise<void>;
  /** Admin-assigned weekend duty roster: local "YYYY-MM-DD" → unit. */
  weekendDuty: Record<string, string>;
  /** Persist an updated weekend duty roster (wraps saveHospitalConfig). */
  saveWeekendDuty: (map: Record<string, string>) => Promise<void>;
  /** Timestamp (Date.now()) set whenever a background config refresh updates the config.
   *  Components with open forms can watch this to show a "Settings updated" banner. */
  configUpdatedAt: number | null;

  // Admin CRUD — wards
  addWard:    (name: string, isIcu: boolean, unit?: string[]) => Promise<void>;
  saveWard:   (ward: WardConfig) => Promise<void>;
  removeWard: (id: string) => Promise<void>;

  // Admin CRUD — lab types
  addLabType:    (name: string, unit: string, alertHigh: number | null, category: string) => Promise<void>;
  saveLabType:   (lab: LabTypeConfig) => Promise<void>;
  removeLabType: (id: string) => Promise<void>;

  // Medication list (for discharge summary autocomplete)
  medications: MedicationConfig[];
  // Admin CRUD — medications
  addMedication:    (med: Omit<MedicationConfig, 'id'>) => Promise<void>;
  saveMedication:   (med: MedicationConfig) => Promise<void>;
  removeMedication: (id: string) => Promise<void>;
  seedMedications:  () => Promise<void>;
}

const ConfigContext = createContext<ConfigContextType | null>(null);

export function useConfig(): ConfigContextType {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error('useConfig must be used within ConfigProvider');
  return ctx;
}

// ─── Provider ───
export const ConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { viewingHospitalId, user } = useAuth();

  const [wards, setWards] = useState<WardConfig[]>(() => {
    return loadFromStorage<WardConfig[]>(WARD_CACHE_KEY) ?? DEFAULT_WARDS;
  });

  const [labTypes, setLabTypes] = useState<LabTypeConfig[]>(() => {
    return loadFromStorage<LabTypeConfig[]>(LAB_CACHE_KEY) ?? DEFAULT_LAB_TYPES;
  });

  const [medications, setMedications] = useState<MedicationConfig[]>(() => {
    return loadFromStorage<MedicationConfig[]>(MED_CACHE_KEY) ?? [];
  });

  const [isLoadingConfig, setIsLoadingConfig] = useState(true);

  const [configUpdatedAt, setConfigUpdatedAt] = useState<number | null>(null);
  const [hospitalConfig, setHospitalConfigState] = useState<HospitalConfig>(
    () => loadFromStorage<HospitalConfig>(HOSPITAL_CONFIG_CACHE_KEY) ?? DEFAULT_HOSPITAL_CONFIG,
  );

  const [unitChiefs, setUnitChiefsState] = useState<Record<string, string>>(
    () => loadFromStorage<Record<string, string>>(UNIT_CHIEFS_CACHE_KEY) ?? {}
  );

  const [templateOverride, setTemplateOverride] = useState<DepartmentTemplateOverride | null>(null);

  const setUnitChief = useCallback(async (unit: string, name: string) => {
    const effectiveHospitalId = viewingHospitalId ?? user?.hospitalId;
    if (!effectiveHospitalId) return;
    await upsertUnitChief(effectiveHospitalId, unit, name);
    setUnitChiefsState(prev => {
      const next = { ...prev, [unit]: name };
      saveToStorage(UNIT_CHIEFS_CACHE_KEY, next);
      return next;
    });
  }, [viewingHospitalId, user?.hospitalId]);

  // Background fetch — re-runs when the logged-in user changes OR when a superadmin
  // switches to a different hospital workspace.
  // IMPORTANT: also depends on user?.id so the fetch re-runs after Supabase restores
  // its auth session (which happens async after mount). Without this dependency the
  // first fetch fires unauthenticated, RLS blocks all rows, and the fallback
  // DEFAULT_HOSPITAL_CONFIG (DEPARTMENT OF MEDICINE) gets cached and shown forever.
  useEffect(() => {
    // Prefer the explicit hospital ID (own user or superadmin override) so the
    // query filter works even before the Supabase auth session is fully hydrated.
    const hid = viewingHospitalId ?? user?.hospitalId ?? undefined;

    const loadAll = () => {
      Promise.all([fetchWards(hid), fetchLabTypes(hid), fetchUnitChiefs(hid), fetchHospitalConfig(hid), fetchMedications(hid)])
        .then(([freshWards, freshLabs, freshChiefs, freshHospital, freshMeds]) => {
          setWards(freshWards);
          setLabTypes(freshLabs);
          setUnitChiefsState(freshChiefs);
          setHospitalConfigState(prev => {
            // Only stamp configUpdatedAt when the config actually changes (not on first load)
            if (prev.hospitalName && prev.hospitalName !== freshHospital.hospitalName) {
              setConfigUpdatedAt(Date.now());
            } else if (JSON.stringify(prev) !== JSON.stringify(freshHospital)) {
              setConfigUpdatedAt(Date.now());
            }
            return freshHospital;
          });
          setMedications(freshMeds);
          // Only cache own-hospital config (not viewed hospitals)
          if (!viewingHospitalId) {
            saveToStorage(WARD_CACHE_KEY, freshWards);
            saveToStorage(LAB_CACHE_KEY, freshLabs);
            saveToStorage(UNIT_CHIEFS_CACHE_KEY, freshChiefs);
            saveToStorage(HOSPITAL_CONFIG_CACHE_KEY, freshHospital);
            saveToStorage(MED_CACHE_KEY, freshMeds);
          }
        })
        .catch(err => console.error('[Config] Failed to load from Supabase — using cache:', err))
        .finally(() => setIsLoadingConfig(false));
    };

    loadAll();

    // Realtime subscription for config tables with exponential-backoff reconnect.
    let destroyed = false;
    let retryTimer: ReturnType<typeof setTimeout>;
    let retryDelay = 2000;
    let ch: ReturnType<typeof supabase.channel>;

    const connectConfig = () => {
      if (destroyed) return;
      ch = supabase.channel(`config-changes-${Date.now()}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'ward_config' }, loadAll)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'lab_type_config' }, loadAll)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'hospital_config' }, loadAll)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'medications' }, loadAll)
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            retryDelay = 2000;
          }
          if ((status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') && !destroyed) {
            supabase.removeChannel(ch);
            retryTimer = setTimeout(() => { if (!destroyed) connectConfig(); }, retryDelay);
            retryDelay = Math.min(retryDelay * 2, 30_000);
          }
        });
    };

    connectConfig();

    return () => {
      destroyed = true;
      clearTimeout(retryTimer);
      supabase.removeChannel(ch);
    };
  }, [viewingHospitalId, user?.id, user?.hospitalId]);

  // Detect specialty from department string
  const activeSpecialty = useMemo<SpecialtyKey>(
    () => detectSpecialtyFromDepartment(hospitalConfig.department),
    [hospitalConfig.department],
  );

  // Fetch department template override whenever hospitalId or specialty changes
  useEffect(() => {
    const hospitalId = viewingHospitalId ?? user?.hospitalId;
    if (!hospitalId) return;
    fetchDepartmentTemplate(hospitalId, activeSpecialty)
      .then(override => setTemplateOverride(override))
      .catch(err => console.error('[Config] Failed to load department template:', err));
  }, [viewingHospitalId, user?.hospitalId, activeSpecialty]);

  // Active field groups: hospital override takes precedence over system default
  const activeFieldGroups = useMemo<SpecialtyFieldGroup[]>(() => {
    if (templateOverride?.fieldGroups?.length) return templateOverride.fieldGroups;
    return SPECIALTY_TEMPLATES[activeSpecialty]?.fieldGroups ?? [];
  }, [templateOverride, activeSpecialty]);

  const saveTemplateOverride = useCallback(async (groups: SpecialtyFieldGroup[]) => {
    const hospitalId = viewingHospitalId ?? user?.hospitalId;
    if (!hospitalId) return;
    const saved = await saveDepartmentTemplate(hospitalId, activeSpecialty, groups);
    setTemplateOverride(saved);
    toast.success('Department template saved');
  }, [viewingHospitalId, user?.hospitalId, activeSpecialty]);

  const resetTemplateOverride = useCallback(async () => {
    const hospitalId = viewingHospitalId ?? user?.hospitalId;
    if (!hospitalId) return;
    await deleteDepartmentTemplate(hospitalId, activeSpecialty);
    setTemplateOverride(null);
    toast.success('Template reset to system default');
  }, [viewingHospitalId, user?.hospitalId, activeSpecialty]);

  const saveHospitalConfig = useCallback(async (config: HospitalConfig) => {
    await upsertHospitalConfig(config);
    setHospitalConfigState(config);
    saveToStorage(HOSPITAL_CONFIG_CACHE_KEY, config);
    toast.success('Hospital settings saved');
  }, []);

  const saveComorbidityMap = useCallback(async (map: ComorbidityEntry[]) => {
    setHospitalConfigState(prev => {
      const next = { ...prev, comorbidityMap: map };
      upsertHospitalConfig(next).catch(err => console.error('[Config] saveComorbidityMap failed:', err));
      saveToStorage(HOSPITAL_CONFIG_CACHE_KEY, next);
      return next;
    });
    toast.success('Comorbidity list saved');
  }, []);

  const saveWeekendDuty = useCallback(async (map: Record<string, string>) => {
    setHospitalConfigState(prev => {
      const next = { ...prev, weekendDuty: map };
      upsertHospitalConfig(next).catch(err => console.error('[Config] saveWeekendDuty failed:', err));
      saveToStorage(HOSPITAL_CONFIG_CACHE_KEY, next);
      return next;
    });
    toast.success('Weekend duty roster saved');
  }, []);

  // ─── Derived values ───
  const icuWardNames = useMemo(
    () => new Set(wards.filter(w => w.isIcu).map(w => w.name)),
    [wards],
  );

  const labTypesByCategory = useMemo(() => {
    const map = new Map<string, LabTypeConfig[]>();
    for (const lt of labTypes.filter(l => l.active)) {
      const group = map.get(lt.category) ?? [];
      group.push(lt);
      map.set(lt.category, group);
    }
    return map;
  }, [labTypes]);

  // ─── Ward mutations ───
  const addWard = useCallback(async (name: string, isIcu: boolean, unit?: string[]) => {
    const maxOrder = wards.reduce((max, w) => Math.max(max, w.sortOrder), -1);
    const effectiveHospitalId = viewingHospitalId ?? user?.hospitalId;
    const created = await createWard(name, isIcu, maxOrder + 1, unit, effectiveHospitalId);
    setWards(prev => {
      const next = [...prev, created];
      saveToStorage(WARD_CACHE_KEY, next);
      return next;
    });
    toast.success(`Ward "${name}" added`);
  }, [wards]);

  const saveWard = useCallback(async (ward: WardConfig) => {
    await updateWard(ward);
    setWards(prev => {
      const next = prev.map(w => w.id === ward.id ? ward : w)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      saveToStorage(WARD_CACHE_KEY, next);
      return next;
    });
    toast.success(`Ward "${ward.name}" updated`);
  }, []);

  const removeWard = useCallback(async (id: string) => {
    await deleteWard(id);
    setWards(prev => {
      const next = prev.filter(w => w.id !== id);
      saveToStorage(WARD_CACHE_KEY, next);
      return next;
    });
    toast.success('Ward removed');
  }, []);

  // ─── Lab Type mutations ───
  const addLabType = useCallback(async (
    name: string, unit: string, alertHigh: number | null, category: string,
  ) => {
    const sameCat = labTypes.filter(l => l.category === category);
    const maxOrder = sameCat.reduce((max, l) => Math.max(max, l.sortOrder), -1);
    const created = await createLabType(name, unit, alertHigh, category, maxOrder + 1);
    setLabTypes(prev => {
      const next = [...prev, created];
      saveToStorage(LAB_CACHE_KEY, next);
      return next;
    });
    toast.success(`Lab type "${name}" added`);
  }, [labTypes]);

  const saveLabType = useCallback(async (lab: LabTypeConfig) => {
    await updateLabType(lab);
    setLabTypes(prev => {
      const next = prev.map(l => l.id === lab.id ? lab : l);
      saveToStorage(LAB_CACHE_KEY, next);
      return next;
    });
    toast.success(`Lab type "${lab.name}" updated`);
  }, []);

  const removeLabType = useCallback(async (id: string) => {
    await deleteLabType(id);
    setLabTypes(prev => {
      const next = prev.filter(l => l.id !== id);
      saveToStorage(LAB_CACHE_KEY, next);
      return next;
    });
    toast.success('Lab type removed');
  }, []);

  // ─── Medication mutations ───
  const addMedication = useCallback(async (med: Omit<MedicationConfig, 'id'>) => {
    const created = await createMedication(med);
    setMedications(prev => {
      const next = [...prev, created].sort((a, b) => a.name.localeCompare(b.name));
      saveToStorage(MED_CACHE_KEY, next);
      return next;
    });
    toast.success(`Medication "${med.name}" added`);
  }, []);

  const saveMedication = useCallback(async (med: MedicationConfig) => {
    await updateMedication(med);
    setMedications(prev => {
      const next = prev.map(m => m.id === med.id ? med : m);
      saveToStorage(MED_CACHE_KEY, next);
      return next;
    });
    toast.success(`Medication "${med.name}" updated`);
  }, []);

  const removeMedication = useCallback(async (id: string) => {
    await deleteMedication(id);
    setMedications(prev => {
      const next = prev.filter(m => m.id !== id);
      saveToStorage(MED_CACHE_KEY, next);
      return next;
    });
    toast.success('Medication removed');
  }, []);

  const seedMedications = useCallback(async () => {
    await seedDefaultMedications();
    const freshMeds = await fetchMedications();
    setMedications(freshMeds);
    saveToStorage(MED_CACHE_KEY, freshMeds);
    toast.success(`${freshMeds.length} medications loaded`);
  }, []);

  const value = useMemo<ConfigContextType>(() => ({
    wards, labTypes, isLoadingConfig,
    icuWardNames, labTypesByCategory,
    unitChiefs, setUnitChief,
    hospitalName: hospitalConfig.hospitalName,
    department: hospitalConfig.department,
    unitOptions: hospitalConfig.units.length > 0 ? hospitalConfig.units : DEFAULT_HOSPITAL_CONFIG.units,
    preOpModuleName: hospitalConfig.preOpModuleName || DEFAULT_HOSPITAL_CONFIG.preOpModuleName,
    procedureListName: hospitalConfig.procedureListName || DEFAULT_HOSPITAL_CONFIG.procedureListName,
    preOpChecklistTemplate: hospitalConfig.preOpChecklistTemplate?.length
      ? hospitalConfig.preOpChecklistTemplate
      : DEFAULT_HOSPITAL_CONFIG.preOpChecklistTemplate,
    showNursingNotes:     hospitalConfig.showNursingNotes     ?? false,
    showMedicationChart:  hospitalConfig.showMedicationChart  ?? false,
    showIntakeOutput:     hospitalConfig.showIntakeOutput     ?? false,
    showBloodTransfusion: hospitalConfig.showBloodTransfusion ?? false,
    showWoundCare:        hospitalConfig.showWoundCare        ?? false,
    showWoundAssessment:  hospitalConfig.showWoundAssessment  ?? false,
    showRehabilitation:   hospitalConfig.showRehabilitation   ?? false,
    showNews2:            hospitalConfig.showNews2            ?? true,
    customTodoShortcuts:  hospitalConfig.customTodoShortcuts  ?? [],
    vitalThresholds:      hospitalConfig.vitalThresholds      ?? DEFAULT_HOSPITAL_CONFIG.vitalThresholds,
    saveHospitalConfig,
    comorbidityMap: hospitalConfig.comorbidityMap ?? [],
    saveComorbidityMap,
    weekendDuty: hospitalConfig.weekendDuty ?? {},
    saveWeekendDuty,
    configUpdatedAt,
    addWard, saveWard, removeWard,
    addLabType, saveLabType, removeLabType,
    medications, addMedication, saveMedication, removeMedication, seedMedications,
    activeSpecialty, activeFieldGroups, templateOverride,
    saveTemplateOverride, resetTemplateOverride,
  }), [
    wards, labTypes, isLoadingConfig,
    icuWardNames, labTypesByCategory,
    unitChiefs, setUnitChief,
    hospitalConfig, saveHospitalConfig, saveComorbidityMap, saveWeekendDuty, configUpdatedAt,
    addWard, saveWard, removeWard,
    addLabType, saveLabType, removeLabType,
    medications, addMedication, saveMedication, removeMedication, seedMedications,
    activeSpecialty, activeFieldGroups, templateOverride,
    saveTemplateOverride, resetTemplateOverride,
  ]);

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
};
